import type { AddressDto, BillingAddressDto, PaymentProfileDto } from "@sticker-giant/sg-frontend-common";
import type { BillingAddressFormValues } from "@/components/checkout/Payment/billingAddressFormSchema";

export const NEW_CARD = "NEW_CARD";

const SAVED_CARD_RADIO_PREFIX = "saved:";

export function sortDefaultCardFirst(cards: PaymentProfileDto[]): PaymentProfileDto[] {
  return [...cards].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

export function toSavedCardRadioValue(id: string): string {
  return `${SAVED_CARD_RADIO_PREFIX}${id}`;
}

export function parseSavedCardRadioValue(value: string): string | undefined {
  if (!value.startsWith(SAVED_CARD_RADIO_PREFIX)) return undefined;
  const id = value.slice(SAVED_CARD_RADIO_PREFIX.length);
  return id || undefined;
}

// The chosen card, if it exists and isn't expired.
export function findSelectableCard(cards: PaymentProfileDto[], id: string | undefined): PaymentProfileDto | undefined {
  return cards.find(card => card.paymentProfileId === id && !card.expired);
}

// The chosen card, else the default. NEW_CARD means none.
export function resolveSelectedSavedCard(
  cards: PaymentProfileDto[],
  selectedSavedCardId: string | undefined,
): PaymentProfileDto | undefined {
  if (selectedSavedCardId === NEW_CARD) return undefined;
  return findSelectableCard(cards, selectedSavedCardId) ?? cards.find(card => card.isDefault && !card.expired);
}

// A delete button's accessible name, so screen readers can tell the cards apart.
export function deleteCardLabel(card: PaymentProfileDto): string {
  return `Delete ${card.cardType || "card"} ending in ${card.last4}`;
}

// A saved card's billing address in the cart's address shape.
export function profileBillingToAddressDto(billing: BillingAddressDto): AddressDto {
  return {
    firstName: billing.firstName,
    lastName: billing.lastName,
    fullName: `${billing.firstName} ${billing.lastName}`.trim(),
    company: billing.company || undefined,
    street1: billing.address,
    city: billing.city,
    state: billing.state,
    postalCode: billing.zip,
    country: billing.country,
    defaultBilling: false,
    defaultShipping: false,
  };
}

// A billing address form in the payment service's shape. The reverse of profileBillingToAddressDto.
export function billingFormToBillingAddressDto(values: BillingAddressFormValues): BillingAddressDto {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    company: values.company ?? "",
    address: [values.street1, values.street2].filter(Boolean).join(", "),
    city: values.city,
    state: values.state ?? "",
    zip: values.postalCode ?? "",
    country: values.country,
  };
}
